import React, { useEffect, useState } from 'react';
import {
  Close,
  DateRange,
  LockOpen,
  Lock,
  FactCheckRounded,
  CalendarMonth,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Typography,
  Divider,
  alpha,
} from '@mui/material';
import { generateFacilityReport } from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import FacilityReportQuaterLock from './FacilityReportQuaterLock';

export default function FacilityReportQuarters({ open, onClose, record }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dialogLock, setDialogLock] = useState(false);
  const [row, setRow] = useState();
  const [refetch, setRefetch] = useState(false);

  const fetchData = () => {
    setLoading(true);
    generateFacilityReport(record?.facilityId, new Date().getFullYear()?.toString())
      .then((response) => {
        const responseData = response?.result?.data;
        setData(responseData);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
        setRefetch(false);
      });
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  useEffect(() => {
    if (refetch) fetchData();
  }, [refetch]);

  const renderContent = () => {
    return (
      <Box sx={{ pb: 2 }}>
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ px: 3, mb: 3 }}
        >
          <Box display="flex" gap={2}>
            <Box
              sx={{
                bgcolor: alpha('#1976d2', 0.1),
                color: 'primary.main',
                p: 1.5,
                borderRadius: '12px',
                display: 'flex',
              }}
            >
              <FactCheckRounded />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {record?.facilityName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Select a quarter to reserve for QAQC recording
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f4f6f8',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
            }}
          >
            <CalendarMonth sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 4, mx: 3 }} />

        {/* Grid Section */}
        <Grid2 container spacing={3} sx={{ px: 3 }}>
          {data?.map((item) => {
            const isReserved = item?.reportType === 'QAQC';

            return (
              <Grid2 key={item?.reportId} size={{ lg: 3, md: 3, sm: 6, xs: 12 }}>
                <Paper
                  elevation={0}
                  onClick={() => {
                    if (!isReserved) {
                      setDialogLock(true);
                      setRow(item);
                    }
                  }}
                  sx={{
                    p: 2.5,
                    cursor: isReserved ? 'default' : 'pointer',
                    borderRadius: '16px',
                    border: '2px solid',
                    borderColor: isReserved ? 'transparent' : alpha('#1976d2', 0.1),
                    bgcolor: isReserved ? '#f8f9fa' : '#ffffff',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': !isReserved
                      ? {
                          borderColor: 'primary.main',
                          boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
                          transform: 'translateY(-4px)',
                        }
                      : {},
                  }}
                >
                  {/* Status Badge */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        bgcolor: isReserved ? alpha('#ffc107', 0.1) : alpha('#2e7d32', 0.1),
                        color: isReserved ? 'text.secondary' : 'success.main',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        {isReserved ? 'QAQC' : 'Available'}
                      </Typography>
                    </Box>
                    {isReserved ? (
                      <Lock sx={{ fontSize: 18, color: 'text.disabled' }} />
                    ) : (
                      <LockOpen sx={{ fontSize: 18, color: 'primary.main' }} />
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <DateRange
                      sx={{
                        fontSize: 60,
                        color: isReserved ? alpha('#9e9e9e', 0.2) : alpha('#1976d2', 0.2),
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={isReserved ? 'text.secondary' : 'text.primary'}
                    >
                      {item?.reportTitle}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        minHeight: '20px',
                        fontStyle: isReserved ? 'italic' : 'normal',
                      }}
                    >
                      {item?.remarks || 'No remarks added'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid2>
            );
          })}
        </Grid2>
      </Box>
    );
  };

  return (
    <Box>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '24px', p: 1 },
        }}
      >
        <Box display="flex" justifyContent="flex-end" p={1}>
          <IconButton onClick={onClose} sx={{ bgcolor: '#f4f6f8' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 1 }}>
          {loading ? (
            <Box sx={{ p: 4 }}>
              <LoaderSkeleton />
            </Box>
          ) : !data || data.length === 0 ? (
            <Box sx={{ py: 8 }}>
              <Empty description="No quarter records found" />
            </Box>
          ) : (
            renderContent()
          )}
        </Box>
      </Dialog>

      <FacilityReportQuaterLock
        open={dialogLock}
        onClose={() => setDialogLock(false)}
        report={row}
        onRefetch={() => setRefetch(true)}
      />
    </Box>
  );
}
